import { createReducer } from 'redux-create-reducer';

import { buildHydrateEvent, HYDRATE_DUMMY } from '@AIZ-GL/hydrate-dummy';

import uuidv1 from 'uuid/v1';

import {
  ABANDON_ADD_ANOTHER_DEVICE,
  ADD_ANOTHER_DEVICE,
  CUSTOMER_DETAILS_FETCHED,
  DELETE_SELECTED_DEVICE,
  DESELECT_ISSUE,
  DESELECT_ALL_ISSUES,
  DEVICE_BRAND_SELECTED,
  DEVICE_COLOR_SELECTED,
  DEVICE_MODEL_SELECTED,
  DEVICE_TYPE_SELECTED,
  ISSUES_FETCHING,
  ISSUES_FETCHED,
  ISSUES_FETCHED_ERROR,
  PRICES_FETCHED,
  RESET_DEVICE_DETAILS,
  SAVE_ISSUES_DESCRIPTION,
  SELECT_ISSUE,
  SET_CONFIGURABLE_FIELDS,
  SET_PROTECTED_DEVICE,
  UNSUPPORTED_PRODUCT_SELECTED,
} from '../actions';

const options = {
  node: [0x01, 0x23, 0x45, 0x67, 0x89, 0xab],
  clockseq: 0x1234,
  msecs: new Date('2011-11-01').getTime(),
  nsecs: 5678,
};

const initialState = {
  archive: [],
  brand: '',
  color: undefined,
  deviceType: undefined,
  id: '',
  imei: '',
  issuesDescription: '',
  issueOptions: [],
  issues: [],
  model: {},
  modelNumber: '',
  prices: {},
  reduxId: uuidv1(options),
  serialNumber: '',
  trackingNumber: '',
  phoneNumber: '',
  loading: false,
};

const dummyData = {
  brand: 'Apple',
  color: 'Black',
  deviceType: 'phone',
  id: '',
  imei: '123456789012345',
  serial_number: '12345',
  phone_number: '5558675309',
  issuesDescription: 'Much Broken.',
  issues: ['screen', 'buttons'],
  reduxId: uuidv1(),
  model: {
    brandName: 'Apple',
    colors: [],
    deviceImages: [],
    id: '2beddasd',
    make: 'phone',
    models: ['iPhone8,2', 'A1687', 'A1634'],
    name: 'iPhone 6S Plus',
    supported: true,
  },
  prices: {
    buttons: 16718,
    screen: 22577,
    total: 39296,
  },
};

const events = {
  [HYDRATE_DUMMY]: buildHydrateEvent(dummyData),

  [ABANDON_ADD_ANOTHER_DEVICE]: state => {
    const { archive } = state;

    const {
      brand,
      color,
      deviceType,
      issues,
      issuesDescription,
      reduxId,
      model,
      prices,
    } = archive.pop();

    return {
      ...state,
      brand,
      color,
      deviceType,
      issues,
      issuesDescription,
      reduxId,
      model,
      prices,
    };
  },

  [ADD_ANOTHER_DEVICE]: state => {
    const {
      archive,
      brand,
      color,
      deviceType,
      id,
      imei,
      phoneNumber,
      modelNumber,
      issueOptions,
      issues,
      issuesDescription,
      model,
      reduxId,
      serialNumber,
      trackingNumber,
      prices,
    } = state;

    const lastDevice = {
      brand,
      color,
      deviceType,
      id,
      imei,
      issues,
      issuesDescription,
      modelNumber,
      phoneNumber,
      reduxId,
      serialNumber,
      trackingNumber,
      model: { ...model },
      prices,
    };

    return {
      ...state,
      ...initialState,
      imei: '',
      serialNumber: '',
      phoneNumber: '',
      archive: [lastDevice].concat(archive.slice(0)),
      issueOptions,
    };
  },

  [DELETE_SELECTED_DEVICE]: (state, { reduxId }) => ({
    ...state,
    archive: state.archive.filter(d => d.reduxId !== reduxId),
  }),

  [DESELECT_ISSUE]: (state, { issueToRemove }) => ({
    ...state,
    issues: state.issues.filter(i => i.name !== issueToRemove.name),
  }),

  [DESELECT_ALL_ISSUES]: state => ({
    ...state,
    issues: [],
  }),

  [DEVICE_BRAND_SELECTED]: (state, { brand }) => ({ ...state, brand }),

  [DEVICE_COLOR_SELECTED]: (state, { color }) => ({ ...state, color }),

  [DEVICE_MODEL_SELECTED]: (state, { model, modelNumber }) => ({
    ...state,
    model: { ...state.model, ...model },
    modelNumber,
    modelConfirmed: modelNumber ? true : false,
  }),

  [UNSUPPORTED_PRODUCT_SELECTED]: (state, { unsupportedProduct }) => ({
    ...state,
    model: {
      id: unsupportedProduct.productId,
      brand: { name: unsupportedProduct.brand },
      make: unsupportedProduct.make,
      name: unsupportedProduct.productName,
    },
  }),

  [DEVICE_TYPE_SELECTED]: (state, { deviceType }) => ({
    ...state,
    brand: '',
    color: undefined,
    deviceType,
    model: {},
  }),

  [PRICES_FETCHED]: (state, { prices }) => ({
    ...state,
    prices,
  }),

  [SAVE_ISSUES_DESCRIPTION]: (state, { description }) => ({
    ...state,
    issuesDescription: description,
  }),

  [SELECT_ISSUE]: (state, { issueToAdd }) => ({
    ...state,
    issues: state.issues.concat(issueToAdd),
  }),

  [ISSUES_FETCHING]: state => ({ ...state, loading: true }),

  [ISSUES_FETCHED]: (state, { issues }) => ({
    ...state,
    loading: false,
    issueOptions: issues.map(({ name, display_name }) => ({
      name,
      displayName: display_name,
    })),
  }),

  [ISSUES_FETCHED_ERROR]: (state, { error }) => ({
    ...state,
    loading: false,
    error,
  }),

  [CUSTOMER_DETAILS_FETCHED]: (
    state,
    {
      imei,
      make,
      model,
      productId,
      brand,
      productName,
      serialNumber,
      trackingNumber,
      phoneNumber,
      carrier,
      capacity,
    }
  ) => ({
    ...state,
    brand,
    deviceType: make,
    imei,
    serialNumber,
    trackingNumber,
    phoneNumber,
    model: {
      ...state.model,
      make,
      models: [],
      id: productId,
      name: productName,
      carrier,
      capacity,
    },
  }),

  [SET_CONFIGURABLE_FIELDS]: (
    state,
    { newConfigurableFields, newCarrier, newCapacity }
  ) => ({
    ...state,
    ...newConfigurableFields,
    model: {
      ...state.model,
      carrier: newCarrier ? newCarrier : state.model.carrier,
      capacity: newCapacity ? newCapacity : state.model.capacity,
    },
  }),

  [SET_PROTECTED_DEVICE]: state => ({ ...state, protectedDevice: true }),

  [RESET_DEVICE_DETAILS]: state => ({
    ...initialState,
    imei: state.imei,
    phoneNumber: state.phoneNumber,
  }),
};

export default createReducer(initialState, events);
